import { useEffect, useState } from "react";
import { getVerification, defaultVerification, type AddressVerification } from "@/lib/verification.functions";

const CACHE_KEY = "woodseer_verification_v1";
// Test/debug hook: when sessionStorage["__wa_force_unverified"] === "1",
// the hook returns an unverified snapshot and skips the network fetch.
// This lets Playwright and admin previews assert unverified-state UX
// (e.g. the booking form gate) without mutating the real DB record.
const FORCE_UNVERIFIED_KEY = "__wa_force_unverified";

function readCache(): AddressVerification | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return { ...defaultVerification, ...(JSON.parse(raw) as Partial<AddressVerification>) };
  } catch {
    return null;
  }
}

function readForceUnverified(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(FORCE_UNVERIFIED_KEY) === "1";
  } catch {
    return false;
  }
}

export function useVerification(): AddressVerification {
  const [value, setValue] = useState<AddressVerification>(defaultVerification);
  useEffect(() => {
    if (readForceUnverified()) {
      setValue({ ...defaultVerification, verified: false });
      return;
    }
    const cached = readCache();
    if (cached) setValue(cached);
    let cancelled = false;
    getVerification()
      .then((r) => {
        if (cancelled) return;
        setValue(r);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(r)); } catch {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return value;
}
